import React, { useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import {
  Table,
  Button,
  List,
  Typography,
  Popconfirm,
  Row,
  Col,
  Popover,
  notification,
} from 'antd'
import { EditOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons'
import { Panel } from '../../../core/components/styled'
import {
  createUrl,
  getQueryParams,
} from '../../../core/utils/services/queryParams'
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE } from '../../../common/constants'
import { useDocumentTitle } from '../../../common/hooks/effects'
import { formatScrapingTags } from '../../utils'
import { SCRAPING_TAG_GROUPS } from '../../graphql/queries'
import { DELETE_SCRAPING_TAG_GROUP } from '../../graphql/mutations'
import {
  ScrapingTagGroupEditorModal,
  ScrapingTagsFilter,
  AliasFilter,
} from './sections'

const ScrapingTagGroupsPage = () => {
  useDocumentTitle('Scraping tag groups')

  const [
    scrapingTagGroupEditorModalSettings,
    setScrapingTagGroupEditorModalSettings,
  ] = useState({
    scrapingTagGroup: null,
    isModalOpen: false,
  })

  const history = useHistory()
  const { pathname, search } = useLocation()
  const { tagIds, alias, page = DEFAULT_PAGE } = getQueryParams(search)

  const {
    data: { scrapingTagGroups = {} } = {},
    loading: isLoadingScrapingTagGroups,
    refetch: refetchScrapingTagGroups,
  } = useQuery(SCRAPING_TAG_GROUPS, {
    variables: {
      page,
      filters: { tagIds, alias: alias?.toString() },
    },
    fetchPolicy: 'no-cache',
  })

  const [deleteScrapingTagGroup, { loading: isDeletingScrapingTag }] =
    useMutation(DELETE_SCRAPING_TAG_GROUP, {
      onCompleted() {
        refetchScrapingTagGroups()
        notification.open({
          message: 'Tag group deleted.',
          type: 'success',
        })
      },
      onError({ message }) {
        notification.open({
          message: 'Failed to delete tag group.',
          description: message,
          type: 'error',
          duration: 0,
        })
      },
    })

  const tableColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: '50px',
    },
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Alias',
      dataIndex: 'alias',
      width: '150px',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: '250px',
    },
    {
      title: 'Created by',
      dataIndex: ['createdBy', 'name'],
      width: '200px',
    },
    {
      title: 'Updated by',
      dataIndex: ['lastModifiedBy', 'name'],
      width: '200px',
    },
    {
      title: 'Test cases',
      dataIndex: 'scrapingTestCaseCount',
      width: '100px',
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      width: '400px',
      render: tags => (
        <List
          size="small"
          split={false}
          dataSource={formatScrapingTags(tags)}
          itemLayout="horizontal"
          renderItem={tag => (
            <List.Item style={{ padding: 0 }}>
              <Typography.Text>{tag}</Typography.Text>
            </List.Item>
          )}
        />
      ),
    },
    {
      title: () => (
        <Button
          type="primary"
          onClick={() =>
            setScrapingTagGroupEditorModalSettings({
              scrapingTagGroup: null,
              isModalOpen: true,
            })
          }
        >
          Create tag group
        </Button>
      ),
      align: 'right',
      render: scrapingTagGroup => (
        <>
          <Popover trigger="hover" content="Edit group">
            <Button
              style={{ marginRight: '15px' }}
              icon={<EditOutlined />}
              onClick={() =>
                setScrapingTagGroupEditorModalSettings({
                  scrapingTagGroup,
                  isModalOpen: true,
                })
              }
            />
          </Popover>
          <Popover trigger="hover" content="Duplicate group">
            <Button
              style={{ marginRight: '15px' }}
              icon={<CopyOutlined />}
              onClick={() =>
                setScrapingTagGroupEditorModalSettings({
                  scrapingTagGroup: {
                    ...scrapingTagGroup,
                    id: null,
                    alias: null,
                    description: null,
                  },
                  isModalOpen: true,
                })
              }
            />
          </Popover>
          <Popconfirm
            title="Are you sure you want to delete this tag group?"
            onConfirm={() =>
              deleteScrapingTagGroup({ variables: { id: scrapingTagGroup.id } })
            }
            placement="left"
            okText="Yes"
            cancelText="No"
          >
            <Popover
              trigger="hover"
              placement="topRight"
              content="Delete group"
            >
              <Button icon={<DeleteOutlined />} />
            </Popover>
          </Popconfirm>
        </>
      ),
    },
  ]

  return (
    <>
      <Panel>
        <Row gutter={24}>
          <Col>
            <ScrapingTagsFilter />
          </Col>
          <Col>
            <AliasFilter />
          </Col>
        </Row>
      </Panel>
      <Table
        loading={isLoadingScrapingTagGroups || isDeletingScrapingTag}
        rowKey="id"
        columns={tableColumns}
        dataSource={scrapingTagGroups.scrapingTagGroups}
        size="small"
        pagination={{
          current: page,
          pageSize: DEFAULT_PAGE_SIZE,
          total: scrapingTagGroups.total,
          onChange: page => history.push(createUrl(pathname, search, { page })),
          showSizeChanger: false,
        }}
      />

      <ScrapingTagGroupEditorModal
        {...scrapingTagGroupEditorModalSettings}
        refetchScrapingTagGroups={refetchScrapingTagGroups}
        closeModal={() =>
          setScrapingTagGroupEditorModalSettings({
            scrapingTagGroup: undefined,
            isModalOpen: false,
          })
        }
      />
    </>
  )
}
export default ScrapingTagGroupsPage
